import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import CompanySelect from '../../FashionOrders/SelectItem/CompanySelect';
import UserSelect from '../../Equipment/Select/UserSelect';
import {
  FINANCIAL_ENTRY_CATEGORY_OPTIONS,
  FINANCIAL_ENTRY_STATUS_OPTIONS,
  FINANCIAL_ENTRY_TYPE_OPTIONS,
} from '../constants';
import FormOptionSelect from './FormOptionSelect';
import { FinancialEntryFormSchemaType } from './schema';
import {
  CardTitle,
  FormGrid,
  FormSearchableSelectWrap,
  StyledCard,
  StyledFormGroup,
} from './style';

const GATEWAY = process.env.REACT_APP_GATEWAY_BASE_URL;
const COMPANIES_API = `${GATEWAY}/companies/companies`;
const USERS_API = `${GATEWAY}/backend/user/findAll`;

interface CompanyOption {
  id: number;
  companyName: string;
}

interface UserOption {
  id: number;
  name: string;
}

const GeneralInfoSection: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FinancialEntryFormSchemaType>();

  const companyId = watch('companyId');
  const companyName = watch('companyName');
  const linkedUserId = watch('linkedUserId');
  const linkedUserName = watch('linkedUserName');

  return (
    <StyledCard>
      <CardTitle>Geral</CardTitle>
      <FormGrid cols={3}>
        <div>
          <Controller
            name="companyId"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.companyId}>
                <CompanySelect<CompanyOption>
                  label="Empresa"
                  apiUrl={COMPANIES_API}
                  displayField="companyName"
                  showDocument={false}
                  value={{ id: companyId ?? 0, companyName: companyName ?? '' }}
                  onChange={(item) => {
                    setValue('companyId', item.id > 0 ? item.id : null, {
                      shouldValidate: true,
                    });
                    setValue('companyName', item.companyName ?? '');
                  }}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="linkedUserId"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.linkedUserId}>
                <UserSelect<UserOption>
                  label="Usuário vinculado"
                  apiUrl={USERS_API}
                  displayField="name"
                  value={
                    linkedUserId
                      ? { id: linkedUserId, name: linkedUserName ?? '' }
                      : null
                  }
                  onChange={(item) => {
                    setValue('linkedUserId', item?.id ?? null, { shouldValidate: true });
                    setValue('linkedUserName', item?.name ?? '');
                  }}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Tipo"
                value={field.value ?? null}
                onChange={field.onChange}
                options={FINANCIAL_ENTRY_TYPE_OPTIONS}
                hasError={!!errors.type}
              />
            )}
          />
        </div>

        <div>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Categoria"
                value={field.value ?? null}
                onChange={field.onChange}
                options={FINANCIAL_ENTRY_CATEGORY_OPTIONS}
                hasError={!!errors.category}
              />
            )}
          />
        </div>

        <div>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Status"
                value={field.value ?? null}
                onChange={field.onChange}
                options={FINANCIAL_ENTRY_STATUS_OPTIONS}
                allowEmpty={false}
                hasError={!!errors.status}
              />
            )}
          />
        </div>

        <StyledFormGroup>
          <label>Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <textarea {...field} placeholder="Descrição do lançamento" />
            )}
          />
        </StyledFormGroup>
      </FormGrid>
    </StyledCard>
  );
};

export default GeneralInfoSection;
